import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { waybillAPI } from '../../api/waybillAPI';

const PHP = (n) =>
  Number(n || 0).toLocaleString('en-PH', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const formatDate = (value) => {
  if (!value) return '';
  return new Date(value).toLocaleDateString('en-PH', {
    month: 'numeric',
    day: 'numeric',
    year: '2-digit',
  });
};

const fmtLong = (dateStr) =>
  new Date(`${dateStr}T00:00:00`).toLocaleDateString('en-PH', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });

const emptyData = { rows: [], vanGroups: {}, grandTotal: 0 };

export default function RevenuePerVanPrintPage() {
  const [searchParams] = useSearchParams();
  const dateFrom = searchParams.get('dateFrom') || new Date().toISOString().split('T')[0];
  const dateTo = searchParams.get('dateTo') || dateFrom;
  const vanNo = searchParams.get('vanNo') || '';
  const wbNo = searchParams.get('wbNo') || '';
  const sealNo = searchParams.get('sealNo') || '';
  const referenceNo = searchParams.get('referenceNo') || '';

  const [data, setData] = useState(emptyData);
  const [loading, setLoading] = useState(true);

  const companyName = useMemo(() => {
    return localStorage.getItem('accountingClientName') || 'ARVANNAH RENZ TRUCKING INC.';
  }, []);

  useEffect(() => {
    waybillAPI.getRevenuePerVan({
      dateFrom,
      dateTo,
      ...(vanNo       ? { vanNo }       : {}),
      ...(wbNo        ? { wbNo }        : {}),
      ...(sealNo      ? { sealNo }      : {}),
      ...(referenceNo ? { referenceNo } : {}),
    })
      .then((res) => {
        setData(res.data?.data || emptyData);
        setLoading(false);
        setTimeout(() => window.print(), 400);
      })
      .catch(() => setLoading(false));
  }, []); // eslint-disable-line

  useEffect(() => {
    const label = dateFrom === dateTo ? fmtLong(dateFrom) : `${dateFrom} to ${dateTo}`;
    document.title = `Revenue Per Van Report — ${label}`;
  }, [dateFrom, dateTo]);

  const vanKeys = Object.keys(data.vanGroups || {}).sort();
  const totalQty = (data.rows || []).reduce((sum, row) => sum + (row.qty || 0), 0);
  const dateLabel = dateFrom === dateTo ? fmtLong(dateFrom) : `${fmtLong(dateFrom)} – ${fmtLong(dateTo)}`;

  if (loading) return <p style={{ padding: 40 }}>Loading report…</p>;

  return (
    <>
      <style>{`
        body { font-family: Arial, sans-serif; font-size: 10pt; margin: 0; color: #111; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        @page { size: A4 landscape; margin: 12mm; }
        @media print { .no-print { display: none !important; } }
        .page { display: block; }
        .header { display: block; text-align: center; margin-bottom: 10px; page-break-after: avoid; }
        h1 { font-size: 14pt; margin: 0; }
        h2 { font-size: 11pt; margin: 2px 0; font-weight: normal; }
        .summary { display: block; margin-bottom: 10px; font-size: 9.5pt; page-break-after: avoid; }
        .summary span { margin-right: 20px; }
        table { width: 100%; border-collapse: collapse; font-size: 9.5pt; }
        th { background: #f0f0f0; border: 1px solid #ddd; padding: 4px 8px; text-align: left; font-size: 9pt; }
        td { border: 1px solid #ddd; padding: 4px 8px; }
        tr:nth-child(even) { background: #fafafa; }
        .van-header { background: #e0f2f1; font-weight: 700; }
        .van-total { background: #fce4ec; font-weight: 700; }
      `}</style>

      <div className="page">
        <div className="header">
          <h1>{companyName}</h1>
          <h2>REVENUE PER VAN REPORT</h2>
          <div style={{ fontSize: '9pt' }}>Period: {dateLabel}</div>
          <div style={{ fontSize: '9pt' }}>{vanNo ? `Van No.: ${vanNo}` : 'All Vans'}</div>
          {wbNo && <div style={{ fontSize: '9pt' }}>WB No.: {wbNo}</div>}
          {sealNo && <div style={{ fontSize: '9pt' }}>Seal No.: {sealNo}</div>}
        </div>

        <div className="summary">
          <span>Total Trips: <strong>{data.rows?.length || 0}</strong></span>
          <span>Grand Total: <strong>₱{PHP(data.grandTotal || 0)}</strong></span>
          <span>Vans: <strong>{vanKeys.length}</strong></span>
        </div>

        <table>
        <thead>
          <tr>
            <th>DATE</th>
            <th>SEAL NO.</th>
            <th>WB NO.</th>
            <th>REFERENCE NO.</th>
            <th>VAN NO.</th>
            <th>CONSIGNEE</th>
            <th style={{ textAlign: 'right' }}>AMOUNT</th>
            <th>REMARKS</th>
            <th style={{ textAlign: 'right' }}>QTY.</th>
          </tr>
        </thead>
        <tbody>
          {(data.rows || []).length === 0 ? (
            <tr>
              <td colSpan={9} style={{ textAlign: 'center', padding: 16 }}>No records found.</td>
            </tr>
          ) : vanNo ? (
            (data.rows || []).map((row) => (
              <tr key={row.waybillId}>
                <td>{formatDate(row.date)}</td>
                <td>{row.sealNo || ''}</td>
                <td>{row.wbNo}</td>
                <td>{row.referenceNumber || ''}</td>
                <td>{row.vanNo}</td>
                <td>{row.consignee}</td>
                <td style={{ textAlign: 'right' }}>
                  {row.amount ? `₱${PHP(row.amount)}` : ''}
                  {row.minimumApplied ? ' *' : ''}
                </td>
                <td>{row.remarks || ''}</td>
                <td style={{ textAlign: 'right' }}>{row.qty || ''}</td>
              </tr>
            ))
          ) : (
            vanKeys.map((key) => {
              const group = data.vanGroups[key];
              const first = group?.rows?.[0] || {};
              return (
                <React.Fragment key={key}>
                  <tr className="van-header">
                    <td colSpan={9}>VAN NO.: {key}   Seal No.: {first.sealNo || '—'}   Plate No.: {first.plateNo || '—'}</td>
                  </tr>
                  {(group.rows || []).map((row) => (
                    <tr key={row.waybillId}>
                      <td>{formatDate(row.date)}</td>
                      <td>{row.sealNo || ''}</td>
                      <td>{row.wbNo}</td>
                      <td>{row.referenceNumber || ''}</td>
                      <td>{row.vanNo}</td>
                      <td>{row.consignee}</td>
                      <td style={{ textAlign: 'right' }}>
                  {row.amount ? `₱${PHP(row.amount)}` : ''}
                  {row.minimumApplied ? ' *' : ''}
                </td>
                      <td>{row.remarks || ''}</td>
                      <td style={{ textAlign: 'right' }}>{row.qty || ''}</td>
                    </tr>
                  ))}
                  <tr className="van-total">
                    <td colSpan={6} style={{ textAlign: 'right' }}>TOTAL</td>
                    <td style={{ textAlign: 'right' }}>₱{PHP(group.totalAmount || 0)}</td>
                    <td />
                    <td style={{ textAlign: 'right' }}>{group.totalQty || 0}</td>
                  </tr>
                </React.Fragment>
              );
            })
          )}
        </tbody>
        {(data.rows || []).length > 0 && (
          <tfoot>
            <tr className="van-total">
              <td colSpan={6} style={{ textAlign: 'right' }}>TOTAL</td>
              <td style={{ textAlign: 'right' }}>₱{PHP(data.grandTotal || 0)}</td>
              <td />
              <td style={{ textAlign: 'right' }}>{totalQty}</td>
            </tr>
          </tfoot>
        )}
        </table>
      </div>

      <div className="no-print" style={{ marginTop: 16 }}>
        <button onClick={() => window.print()} style={{ padding: '6px 16px' }}>Print</button>
      </div>
    </>
  );
}
